import Answer from 'domains/models/answer';
import { AnswerType } from 'domains/models/answer/answerType';
import { formatToISO } from 'infras/datetime';
import { z } from 'zod';
import { WorkflowTypeEnum } from '../workflow/enum';
import { AnswerTypeEnum } from './enum';

export type AnswerListItem = z.infer<typeof schemaAnswerListItem>;
export const schemaAnswerListItem = z.object({
  id: z.number(),
  answerType: AnswerTypeEnum,
  userId: z.string().nullable(),
  uid: z.string(),
  question: z.string(),
  content: z.union([z.string(), z.array(z.string())]).nullable(),
  contentId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type RoutineAnswerCreateBody = z.infer<
  typeof schemaRoutineAnswerCreateBody
>;
export const schemaRoutineAnswerCreateBody = z.object({
  routineUid: z.string(),
  routineBatchUid: z.string(),
  workflowType: WorkflowTypeEnum,
  content: z.string().optional(),
  question: z.string().optional(),
});

export type FormAnswerCreateBodyItem = z.infer<
  typeof schemaFormAnswerCreateBodyItem
>;
export const schemaFormAnswerCreateBodyItem = z.object({
  formId: z.number(),
  question: z.string(),
  answerType: AnswerTypeEnum,
  content: z.union([z.string(), z.array(z.string())]),
  contentId: z.union([z.string(), z.array(z.string())]).nullable(),
  workflowId: z.string(),
});

export type FormAnswerUpdateBodyItem = z.infer<
  typeof schemaFormAnswerUpdateBodyItem
>;
export const schemaFormAnswerUpdateBodyItem = z.object({
  id: z.number(),
  question: z.string(),
  answerType: AnswerTypeEnum,
  content: z.union([z.string(), z.array(z.string())]),
  contentId: z.union([z.string(), z.array(z.string())]).nullable(),
  workflowId: z.string(),
});

export type FormAnswerCreateBody = z.infer<typeof schemaFormAnswerCreateBody>;
export const schemaFormAnswerCreateBody = z.object({
  email: z.string().nullable(),
  name: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  locale: z.string(),
  answers: z.array(schemaFormAnswerCreateBodyItem),
});

export type FormAnswerUpdateBody = z.infer<typeof schemaFormAnswerUpdateBody>;
export const schemaFormAnswerUpdateBody = z.object({
  email: z.string().nullable(),
  name: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  locale: z.string(),
  answers: z.array(schemaFormAnswerUpdateBodyItem),
});

export type FormAnswerCreateResultItem = z.infer<
  typeof schemaFormAnswerCreateResultItem
>;
export const schemaFormAnswerCreateResultItem = z.object({
  uid: z.string(),
  redirectUrl: z.string().nullable(),
  forwardablePath: z.string().nullable(),
});

export const toListItem = (answer: Answer) => {
  let content = answer.content;
  let contentId = answer.contentId;
  if (answer.answerType === AnswerType.MultiChoice) {
    if (content) {
      try {
        content = JSON.parse(content);
      } catch (error) {
        console.log('answer content json parse failed', answer);
        console.error(error);
      }
    }
    if (contentId) {
      try {
        contentId = JSON.parse(contentId);
      } catch (error) {
        console.log('answer contentId json parse failed', answer);
        console.error(error);
      }
    }
  }

  const listItem: AnswerListItem = {
    id: answer.id!,
    answerType: answer.answerType,
    userId: answer.userId,
    uid: answer.uid,
    question: answer.question,
    content,
    contentId,
    createdAt: formatToISO(answer.createdAt),
    updatedAt: formatToISO(answer.updatedAt),
  };
  return listItem;
};

export const toItem = (answer: Answer) => {
  return toListItem(answer);
};

export const formatGuestAnswer = (answer: AnswerListItem | Answer) => {
  if (
    answer.answerType === AnswerType.MultiChoice &&
    typeof answer.content === 'string'
  ) {
    const answers = JSON.parse(answer.content);
    return answers.join(' / ');
  }
  return answer.content;
};

export const filterAnswerList = (answers: AnswerListItem[]) => {
  return answers.filter((a) => a.answerType !== AnswerType.Skip);
};
